import AdminPageBackground from "@/components/admin/AdminPageBackground";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useCategories } from "@/hooks/useCategories";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { 
  ArrowLeft, Globe, RefreshCw, Calendar, MapPin, ExternalLink, 
  Check, X, Loader2, Plus, Trash2, Clock, Pencil, Link as LinkIcon
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";

interface ScrapeSource {
  id: string;
  name: string;
  url: string;
  category: string;
  is_active: boolean;
  last_scraped_at: string | null;
  scrape_frequency: string;
  auto_publish: boolean;
}

interface ScrapedEvent {
  id: string;
  source_name: string;
  title: string;
  title_ar: string | null;
  description: string | null;
  date: string | null;
  location: string | null;
  image_url: string | null;
  category: string | null;
  external_link: string | null;
  is_published: boolean;
  scraped_at: string;
}

const EventsImport = () => {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const { categories } = useCategories();
  const [sources, setSources] = useState<ScrapeSource[]>([]);
  const [events, setEvents] = useState<ScrapedEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [scraping, setScraping] = useState<string | null>(null);
  const [newSource, setNewSource] = useState({ name: "", url: "", category: "events" });
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<ScrapedEvent | null>(null);
  const [isEditSourceDialogOpen, setIsEditSourceDialogOpen] = useState(false);
  const [editingSource, setEditingSource] = useState<ScrapeSource | null>(null);
  const [editSourceForm, setEditSourceForm] = useState({ name: "", url: "", category: "events", auto_publish: false });
  const [editForm, setEditForm] = useState({
    title: "",
    title_ar: "",
    description: "",
    date: "",
    location: "",
    image_url: "",
    external_link: "",
    category: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    const [sourcesRes, eventsRes] = await Promise.all([
      supabase.from("scrape_sources").select("*").order("created_at"),
      supabase.from("scraped_events").select("*").order("scraped_at", { ascending: false }).limit(100),
    ]);

    if (sourcesRes.data) setSources(sourcesRes.data);
    if (eventsRes.data) setEvents(eventsRes.data);
    setLoading(false);
  };

  const handleScrape = async (sourceId?: string) => {
    setScraping(sourceId || "all");
    try {
      const { data, error } = await supabase.functions.invoke("scrape-events", {
        body: sourceId ? { sourceId } : {},
      });

      if (error) throw error;

      if (data?.success) {
        const totalEvents = data.results?.reduce((sum: number, r: any) => sum + r.eventsFound, 0) || 0;
        toast.success(
          isRTL 
            ? `تم جلب ${totalEvents} فعالية بنجاح` 
            : `Successfully scraped ${totalEvents} events`
        );
        fetchData();
      } else {
        throw new Error(data?.error || "Scrape failed");
      }
    } catch (error) {
      console.error("Scrape error:", error);
      toast.error(isRTL ? "حدث خطأ أثناء الجلب" : "Error during scraping");
    } finally {
      setScraping(null);
    }
  };

  const toggleSourceActive = async (source: ScrapeSource) => {
    const { error } = await supabase
      .from("scrape_sources")
      .update({ is_active: !source.is_active })
      .eq("id", source.id);

    if (!error) {
      setSources(sources.map(s => s.id === source.id ? { ...s, is_active: !s.is_active } : s));
    }
  };

  const toggleSourceAutoPublish = async (source: ScrapeSource) => {
    const { error } = await supabase
      .from("scrape_sources")
      .update({ auto_publish: !source.auto_publish })
      .eq("id", source.id);

    if (!error) {
      setSources(sources.map(s => s.id === source.id ? { ...s, auto_publish: !s.auto_publish } : s));
      toast.success(
        !source.auto_publish 
          ? (isRTL ? "سيتم نشر الفعاليات تلقائياً" : "Events will be auto-published") 
          : (isRTL ? "تم إيقاف النشر التلقائي" : "Auto-publish disabled")
      );
    }
  };

  const toggleEventPublished = async (event: ScrapedEvent) => {
    const { error } = await supabase
      .from("scraped_events")
      .update({ is_published: !event.is_published })
      .eq("id", event.id);

    if (!error) {
      setEvents(events.map(e => e.id === event.id ? { ...e, is_published: !e.is_published } : e));
      toast.success(
        event.is_published 
          ? (isRTL ? "تم إلغاء النشر" : "Unpublished") 
          : (isRTL ? "تم النشر" : "Published")
      );
    }
  };

  const deleteEvent = async (eventId: string) => {
    const { error } = await supabase.from("scraped_events").delete().eq("id", eventId);
    if (!error) {
      setEvents(events.filter(e => e.id !== eventId));
      toast.success(isRTL ? "تم الحذف" : "Deleted");
    }
  };

  const addSource = async () => {
    if (!newSource.name || !newSource.url) return;

    const { error } = await supabase.from("scrape_sources").insert({
      name: newSource.name,
      url: newSource.url,
      category: newSource.category,
    });

    if (!error) {
      toast.success(isRTL ? "تم إضافة المصدر" : "Source added");
      setNewSource({ name: "", url: "", category: "events" });
      setIsAddDialogOpen(false);
      fetchData();
    }
  };

  const deleteSource = async (sourceId: string) => {
    const { error } = await supabase.from("scrape_sources").delete().eq("id", sourceId);
    if (!error) {
      setSources(sources.filter(s => s.id !== sourceId));
      toast.success(isRTL ? "تم حذف المصدر" : "Source deleted");
    }
  };

  const openEditSourceDialog = (source: ScrapeSource) => {
    setEditingSource(source);
    setEditSourceForm({
      name: source.name,
      url: source.url,
      category: source.category,
      auto_publish: source.auto_publish || false,
    });
    setIsEditSourceDialogOpen(true);
  };

  const updateSource = async () => {
    if (!editingSource || !editSourceForm.name || !editSourceForm.url) return;
    
    const oldSourceName = editingSource.name;
    const newSourceName = editSourceForm.name;
    
    // Update the source
    const { error } = await supabase
      .from("scrape_sources")
      .update({
        name: newSourceName,
        url: editSourceForm.url,
        category: editSourceForm.category,
        auto_publish: editSourceForm.auto_publish,
      })
      .eq("id", editingSource.id);

    if (!error) {
      // Also update source_name in all related events if name changed
      if (oldSourceName !== newSourceName) {
        await supabase
          .from("scraped_events")
          .update({ source_name: newSourceName })
          .eq("source_name", oldSourceName);
        
        // Update local events state
        setEvents(events.map(e => 
          e.source_name === oldSourceName ? { ...e, source_name: newSourceName } : e
        ));
      }
      
      setSources(sources.map(s => 
        s.id === editingSource.id ? { ...s, ...editSourceForm } : s
      ));
      toast.success(isRTL ? "تم تحديث المصدر" : "Source updated");
      setIsEditSourceDialogOpen(false);
      setEditingSource(null);
    } else {
      toast.error(isRTL ? "حدث خطأ أثناء التحديث" : "Error updating source");
    }
  };

  const openEditDialog = (event: ScrapedEvent) => {
    setEditingEvent(event);
    setEditForm({
      title: event.title || "",
      title_ar: event.title_ar || "",
      description: event.description || "",
      date: event.date || "",
      location: event.location || "",
      image_url: event.image_url || "",
      external_link: event.external_link || "",
      category: event.category || "",
    });
    setIsEditDialogOpen(true);
  };

  const updateEvent = async () => {
    if (!editingEvent) return;
    
    const { error } = await supabase
      .from("scraped_events")
      .update({
        title: editForm.title,
        title_ar: editForm.title_ar || null,
        description: editForm.description || null,
        date: editForm.date || null,
        location: editForm.location || null,
        image_url: editForm.image_url || null,
        external_link: editForm.external_link || null,
        category: editForm.category || null,
      })
      .eq("id", editingEvent.id);

    if (!error) {
      setEvents(events.map(e => 
        e.id === editingEvent.id ? { ...e, ...editForm } : e
      ));
      toast.success(isRTL ? "تم تحديث الفعالية" : "Event updated");
      setIsEditDialogOpen(false);
      setEditingEvent(null);
    } else {
      toast.error(isRTL ? "حدث خطأ أثناء التحديث" : "Error updating event");
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString(isRTL ? "ar-SA" : "en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <AdminPageBackground className={isRTL ? "rtl" : "ltr"}>
      <nav className="border-b border-white/10 bg-white/5 backdrop-blur-sm w-full">
        <div className="max-w-[2000px] mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link
              to="/admin-dashboard"
              className="flex items-center gap-2 text-primary hover:text-primary/80 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              {isRTL ? "العودة للوحة التحكم" : "Back to Dashboard"}
            </Link>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => i18n.changeLanguage(i18n.language === "en" ? "ar" : "en")}
              className="rounded-full"
            >
              <Globe className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </nav>

      <main className="max-w-[2000px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold">
            {isRTL ? "استيراد الفعاليات" : "Events Import"}
          </h1>
          <Button 
            onClick={() => handleScrape()} 
            disabled={scraping !== null}
            className="flex items-center gap-2"
          >
            {scraping === "all" ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
            {isRTL ? "جلب من جميع المصادر" : "Scrape All Sources"}
          </Button>
        </div>

        <Tabs defaultValue="sources" className="space-y-6">
          <TabsList>
            <TabsTrigger value="sources">
              {isRTL ? "المصادر" : "Sources"} ({sources.length})
            </TabsTrigger>
            <TabsTrigger value="events">
              {isRTL ? "الفعاليات المستوردة" : "Imported Events"} ({events.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="sources">
            <Card className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">
                  {isRTL ? "مصادر الجلب" : "Scrape Sources"}
                </h2>
                <Button onClick={() => setIsAddDialogOpen(true)} size="sm">
                  <Plus className="w-4 h-4 mr-1" />
                  {isRTL ? "إضافة مصدر" : "Add Source"}
                </Button>
              </div>

              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin" />
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{isRTL ? "الاسم" : "Name"}</TableHead>
                      <TableHead>{isRTL ? "الرابط" : "URL"}</TableHead>
                      <TableHead>{isRTL ? "التصنيف" : "Category"}</TableHead>
                      <TableHead>{isRTL ? "آخر جلب" : "Last Scraped"}</TableHead>
                      <TableHead>{isRTL ? "مفعل" : "Active"}</TableHead>
                      <TableHead>{isRTL ? "نشر تلقائي" : "Auto Publish"}</TableHead>
                      <TableHead>{isRTL ? "الإجراءات" : "Actions"}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sources.map((source) => (
                      <TableRow key={source.id}>
                        <TableCell className="font-medium">{source.name}</TableCell>
                        <TableCell>
                          <a 
                            href={source.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-primary hover:underline flex items-center gap-1"
                          >
                            {source.url.slice(0, 30)}...
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary">{source.category}</Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {source.last_scraped_at ? (
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {formatDate(source.last_scraped_at)}
                            </span>
                          ) : (
                            "-"
                          )}
                        </TableCell>
                        <TableCell>
                          <Switch 
                            checked={source.is_active} 
                            onCheckedChange={() => toggleSourceActive(source)} 
                          />
                        </TableCell>
                        <TableCell>
                          <Switch 
                            checked={source.auto_publish || false} 
                            onCheckedChange={() => toggleSourceAutoPublish(source)} 
                          />
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openEditSourceDialog(source)}
                              title={isRTL ? "تعديل" : "Edit"}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleScrape(source.id)}
                              disabled={scraping !== null}
                            >
                              {scraping === source.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <RefreshCw className="w-4 h-4" />
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => deleteSource(source.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="events">
            <Card className="p-6">
              <h2 className="text-xl font-semibold mb-4">
                {isRTL ? "الفعاليات المستوردة" : "Imported Events"}
              </h2>

              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin" />
                </div>
              ) : events.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  {isRTL ? "لا توجد فعاليات مستوردة بعد" : "No imported events yet"}
                </div>
              ) : (
              <div className="space-y-3">
                  {events.map((event) => (
                    <div 
                      key={event.id} 
                      className="flex items-center gap-4 p-4 border rounded-lg hover:bg-muted/50 transition-colors bg-white"
                    >
                      {/* Image */}
                      <div className="shrink-0">
                        {event.image_url ? (
                          <img 
                            src={event.image_url} 
                            alt={event.title}
                            className="w-16 h-16 object-cover rounded-lg"
                          />
                        ) : (
                          <div className="w-16 h-16 bg-muted rounded-lg flex items-center justify-center">
                            <Calendar className="w-6 h-6 text-muted-foreground" />
                          </div>
                        )}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold text-sm truncate max-w-[300px]">
                            {isRTL && event.title_ar ? event.title_ar : event.title}
                          </h3>
                          <Badge variant={event.is_published ? "default" : "secondary"} className="text-xs">
                            {event.is_published 
                              ? (isRTL ? "منشور" : "Published") 
                              : (isRTL ? "مسودة" : "Draft")}
                          </Badge>
                          {event.category && (
                            <Badge variant="outline" className="text-xs">
                              {event.category}
                            </Badge>
                          )}
                        </div>
                        
                        <div className="flex items-center gap-3 mt-1.5 text-xs text-muted-foreground flex-wrap">
                          {event.date && (
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {event.date}
                            </span>
                          )}
                          {event.location && (
                            <span className="flex items-center gap-1 truncate max-w-[150px]">
                              <MapPin className="w-3 h-3 shrink-0" />
                              <span className="truncate">{event.location}</span>
                            </span>
                          )}
                          <Badge variant="secondary" className="text-xs">
                            {event.source_name}
                          </Badge>
                        </div>

                        {/* External Link - Truncated */}
                        {event.external_link && (
                          <a 
                            href={event.external_link} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 mt-1.5 text-xs text-primary hover:underline max-w-[250px]"
                          >
                            <LinkIcon className="w-3 h-3 shrink-0" />
                            <span className="truncate" dir="ltr">
                              {new URL(event.external_link).hostname}
                            </span>
                          </a>
                        )}
                      </div>

                      {/* Action Buttons - Always Visible */}
                      <div className="flex items-center gap-2 shrink-0">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openEditDialog(event)}
                          className="gap-1"
                        >
                          <Pencil className="w-4 h-4" />
                          <span className="hidden sm:inline text-xs">
                            {isRTL ? "تعديل" : "Edit"}
                          </span>
                        </Button>
                        <Button
                          size="sm"
                          variant={event.is_published ? "secondary" : "default"}
                          onClick={() => toggleEventPublished(event)}
                          className="gap-1"
                        >
                          {event.is_published ? (
                            <>
                              <X className="w-4 h-4" />
                              <span className="hidden sm:inline text-xs">
                                {isRTL ? "إلغاء" : "Unpublish"}
                              </span>
                            </>
                          ) : (
                            <>
                              <Check className="w-4 h-4" />
                              <span className="hidden sm:inline text-xs">
                                {isRTL ? "نشر" : "Publish"}
                              </span>
                            </>
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => deleteEvent(event.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{isRTL ? "إضافة مصدر جديد" : "Add New Source"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-1 block">
                {isRTL ? "اسم المصدر" : "Source Name"}
              </label>
              <Input
                value={newSource.name}
                onChange={(e) => setNewSource({ ...newSource, name: e.target.value })}
                placeholder={isRTL ? "مثال: موقع الفعاليات" : "e.g., Events Website"}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">
                {isRTL ? "رابط الموقع" : "Website URL"}
              </label>
              <Input
                value={newSource.url}
                onChange={(e) => setNewSource({ ...newSource, url: e.target.value })}
                placeholder="https://example.com/events"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">
                {isRTL ? "التصنيف" : "Category"}
              </label>
              <select
                value={newSource.category}
                onChange={(e) => setNewSource({ ...newSource, category: e.target.value })}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {categories?.map((cat) => (
                  <option key={cat.id} value={cat.category_slug || cat.path.replace('/', '').replace('/category/', '')}>
                    {isRTL ? cat.name_ar : cat.name_en}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
                {isRTL ? "إلغاء" : "Cancel"}
              </Button>
              <Button onClick={addSource}>
                {isRTL ? "إضافة" : "Add"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{isRTL ? "تعديل الفعالية" : "Edit Event"}</DialogTitle>
          </DialogHeader>
          {editingEvent && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1 block">
                    {isRTL ? "العنوان (إنجليزي)" : "Title (English)"}
                  </label>
                  <Input
                    value={editForm.title}
                    onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                    placeholder={isRTL ? "عنوان الفعالية" : "Event title"}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">
                    {isRTL ? "العنوان (عربي)" : "Title (Arabic)"}
                  </label>
                  <Input
                    value={editForm.title_ar}
                    onChange={(e) => setEditForm({ ...editForm, title_ar: e.target.value })}
                    placeholder={isRTL ? "عنوان الفعالية بالعربي" : "Event title in Arabic"}
                    dir="rtl"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium mb-1 block">
                  {isRTL ? "الوصف" : "Description"}
                </label>
                <textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  placeholder={isRTL ? "وصف الفعالية" : "Event description"}
                  className="w-full min-h-[100px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1 block">
                    {isRTL ? "التاريخ" : "Date"}
                  </label>
                  <Input
                    value={editForm.date}
                    onChange={(e) => setEditForm({ ...editForm, date: e.target.value })}
                    placeholder="2025-01-15"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">
                    {isRTL ? "الموقع" : "Location"}
                  </label>
                  <Input
                    value={editForm.location}
                    onChange={(e) => setEditForm({ ...editForm, location: e.target.value })}
                    placeholder={isRTL ? "موقع الفعالية" : "Event location"}
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium mb-1 block">
                  {isRTL ? "رابط الصورة" : "Image URL"}
                </label>
                <Input
                  value={editForm.image_url}
                  onChange={(e) => setEditForm({ ...editForm, image_url: e.target.value })}
                  placeholder="https://example.com/image.jpg"
                  dir="ltr"
                />
                {editForm.image_url && (
                  <div className="mt-2">
                    <img 
                      src={editForm.image_url} 
                      alt="Preview"
                      className="w-32 h-20 object-cover rounded-lg border"
                      onError={(e) => (e.currentTarget.style.display = 'none')}
                    />
                  </div>
                )}
              </div>

              <div>
                <label className="text-sm font-medium mb-1 block">
                  {isRTL ? "التصنيف" : "Category"}
                </label>
              <select
                value={editForm.category}
                onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <option value="">{isRTL ? "بدون تصنيف" : "No category"}</option>
                {categories?.map((cat) => (
                  <option key={cat.id} value={cat.category_slug || cat.path.replace('/', '').replace('/category/', '')}>
                    {isRTL ? cat.name_ar : cat.name_en}
                  </option>
                ))}
              </select>
              </div>

              <div>
                <label className="text-sm font-medium mb-1 block">
                  {isRTL ? "رابط التسجيل" : "Registration Link"}
                </label>
                <Input
                  value={editForm.external_link}
                  onChange={(e) => setEditForm({ ...editForm, external_link: e.target.value })}
                  placeholder="https://example.com/register"
                  dir="ltr"
                />
                {editForm.external_link && (
                  <div className="mt-2 p-2 bg-primary/5 rounded">
                    <a 
                      href={editForm.external_link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-sm text-primary hover:underline flex items-center gap-1"
                    >
                      <ExternalLink className="w-3 h-3" />
                      {isRTL ? "معاينة الرابط" : "Preview link"}
                    </a>
                  </div>
                )}
              </div>
              
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                  {isRTL ? "إلغاء" : "Cancel"}
                </Button>
                <Button onClick={updateEvent}>
                  {isRTL ? "حفظ التغييرات" : "Save Changes"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Source Dialog */}
      <Dialog open={isEditSourceDialogOpen} onOpenChange={setIsEditSourceDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{isRTL ? "تعديل المصدر" : "Edit Source"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-1 block">
                {isRTL ? "اسم المصدر" : "Source Name"}
              </label>
              <Input
                value={editSourceForm.name}
                onChange={(e) => setEditSourceForm({ ...editSourceForm, name: e.target.value })}
                placeholder={isRTL ? "اسم المصدر" : "Source name"}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">
                {isRTL ? "الرابط" : "URL"}
              </label>
              <Input
                value={editSourceForm.url}
                onChange={(e) => setEditSourceForm({ ...editSourceForm, url: e.target.value })}
                placeholder="https://example.com/events"
                dir="ltr"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">
                {isRTL ? "التصنيف" : "Category"}
              </label>
              <select
                value={editSourceForm.category}
                onChange={(e) => setEditSourceForm({ ...editSourceForm, category: e.target.value })}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {categories?.map((cat) => (
                  <option key={cat.id} value={cat.category_slug || cat.path.replace('/', '').replace('/category/', '')}>
                    {isRTL ? cat.name_ar : cat.name_en}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
              <div>
                <label className="text-sm font-medium">
                  {isRTL ? "نشر تلقائي" : "Auto Publish"}
                </label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {isRTL ? "نشر الفعاليات الجديدة تلقائياً عند الجلب" : "Auto-publish new events when scraped"}
                </p>
              </div>
              <Switch 
                checked={editSourceForm.auto_publish} 
                onCheckedChange={(checked) => setEditSourceForm({ ...editSourceForm, auto_publish: checked })} 
              />
            </div>
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setIsEditSourceDialogOpen(false)}>
                {isRTL ? "إلغاء" : "Cancel"}
              </Button>
              <Button onClick={updateSource}>
                {isRTL ? "حفظ التغييرات" : "Save Changes"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </AdminPageBackground>
  );
};

export default EventsImport;
